import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Sparkles, Send, Square, Loader2, ShieldAlert, CheckCircle2, Plus, ChevronDown, ArrowLeft, X, ListChecks, AlertTriangle, History } from 'lucide-react';
import { apiFetch, ApiClientError, API_BASE, handleUnauthorized } from '@/lib/api';
import { postSSE } from '@/lib/sse';
import { toast } from '@/stores/toast';
import { formatElapsedDuration, formatElapsedSince } from '@/lib/format';
import { isNearBottom } from '@/lib/chat-scroll';
import { convertPdfToPngPages } from '@/lib/pdf-to-images';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { ConfirmDialog } from '@/components/Modal';
import { FilePickerField } from '@/components/FilePickerField';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';

/**
 * `/settings/ai-setup` (v0.4 章⑥ F-6)。AI とのヒアリング対話から、シフト・労務まわりの設定を
 * 提案 → まとめて適用する画面。
 *
 * D-92: 「AI が直接書き込む」の意味は書込み経路が POST .../apply の1本だけであることであり、
 * チャット中も提案カードもシステムの語彙 (target_kind/target_key/proposed_value 等) は一切見せず、
 * 業務の言葉で機械生成された explanation だけを表示する (§6-2b)。
 *
 * ⚠️ staging実機検証指摘 (2026-08-24) 対応: 当初は「セッション一覧/詳細APIが無いため画面遷移で
 * 会話が消える」設計だったが、GET /ai-setup/sessions・GET /ai-setup/sessions/:id/messages を追加し
 * 解消した (会話ログ自体は元々 `ai_setup_messages` に永続化されていた、失われていたのは閲覧・再開
 * 手段のみ)。あわせて「初回導入なのか設定変更なのか分かりにくい」指摘に対し、入口を
 * 「初めての方向け (scope=all を既定として強調)」と「特定の設定だけ変更」の2段に再構成した。
 * 「設定の全量に対する進捗」は網羅の定義がテナント・業態ごとに異なり定量化できないため、代わりに
 * 過去セッションの件数・適用済み提案数という実測可能な実績で「これまで何をしたか」を可視化する
 * (§6-0 原則: 存在しない完了率を捏造しない)。
 *
 * 設計: docs/design/v04-shift-labor-management-detail.md §6-2 / §6-2b / §6-3
 */

type Scope = 'shift' | 'staffing' | 'attendance' | 'compensation' | 'leave' | 'request' | 'competency' | 'all';
type SessionStatus = 'in_progress' | 'proposed' | 'applied' | 'abandoned';
type ChatMessage = { id: string; role: 'user' | 'assistant'; text: string };
type ProposalStatus = 'proposed' | 'accepted' | 'rejected' | 'applied' | 'applying' | 'superseded';
type Proposal = {
  proposal_id: string;
  target_kind: string;
  explanation: string;
  status: ProposalStatus;
  ai_write_tier: 'standard' | 'sensitive';
  confirmed_at: string | null;
  applied_at: string | null;
  created_at: string;
};
type ProposalsResponse = { proposals: Proposal[] };
type ApplyResponse = {
  applied: Array<{ proposal_id: string; target_kind: string; target_table: string; target_row_id: string }>;
  // ⚠️ §8-110: 会話中にAIが同じ設定を複数回提案した場合等、1件の提案だけが適用できないことがある。
  // 以前は1件の失敗が他の正当な提案も道連れにして全滅させていたため、個別の成否を返すようにした。
  failed: Array<{ proposal_id: string; target_kind: string; message: string }>;
};
type SessionSummary = {
  session_id: string;
  scope: Scope;
  status: SessionStatus;
  started_by_user_id: string;
  started_by_display_name: string | null;
  created_at: string;
  updated_at: string;
  proposal_counts: { total: number; proposed: number; applied: number };
};
type SessionsResponse = { sessions: SessionSummary[] };
type SessionMessagesResponse = {
  session_id: string; scope: Scope; status: SessionStatus;
  messages: Array<{ message_id: string; role: 'user' | 'assistant'; text: string; created_at: string }>;
};
// v0.4「設定網羅性チェック」: ①②とも pending が1件でも残っていると POST .../apply がブロックされる
// (AI_SETUP_TOPICS_UNRESOLVED)。件数ではなく具体的な論点名を出す (§6-0: 存在しない完了率を捏造しない、
// 有限個の名前付きトピックの一覧なのでこの原則には抵触しない)。
type TopicStatusItem = {
  topic_key: string; topic_type: 'field_coverage' | 'branch_confirmation'; session_scope: Scope;
  // 'deferred': 依存先の分岐確認トピックが未解決のためブロック対象外だが「確認済み」でもない
  // (バックエンド lib/ai-setup/topic-coverage.ts と同じ意味論、3並列レビュー指摘 #2)。
  // pendingTopics のフィルタ (status==='pending') には元々含まれないため画面挙動は変わらない。
  label: string; status: 'pending' | 'satisfied' | 'not_applicable' | 'addressed' | 'deferred'; note: string | null;
};
type TopicsResponse = { topics: TopicStatusItem[] };

// PDF/画像添付読み取り (v0.5、Full限定)。設計: docs/design/v05-ai-setup-attachment-ingestion-detail.md
type AttachmentJobStatus = 'uploaded' | 'queued' | 'processing' | 'completed' | 'failed' | 'cancelled';
type AttachmentJob = {
  job_id: string; status: AttachmentJobStatus; page_count: number | null;
  error_message: string | null; created_at: string; updated_at: string;
};
// §6: 全ページを1回のuser messageに積む設計のため (AI-OCRの1ページ1回とは異なる)、バックエンドと
// 同じ上限をここでも明示し、変換前にクライアント側で早期に弾く (routes/ai-setup.ts の
// MAX_ATTACHMENT_PAGES と同値を維持すること)。
const MAX_ATTACHMENT_PAGES = 20;
// ⚠️ 実装完了後レビュー指摘 (UX Major): 原本PDFはクライアント側でのみ変換されバックエンドへは送らないため、
// サイズ上限もクライアント側で持つ必要がある (AI-OCRの MAX_PDF_SIZE_BYTES と同値、§6)。無制限だと
// 巨大PDFの変換処理でブラウザが固まって見える。
const MAX_ATTACHMENT_FILE_SIZE_BYTES = 30 * 1024 * 1024;
const ATTACHMENT_TERMINAL_STATUSES: AttachmentJobStatus[] = ['completed', 'failed', 'cancelled'];

const SCOPE_OPTIONS: Array<{ value: Scope; label: string }> = [
  { value: 'shift', label: 'シフト管理の設定 (期間・締切・パターン等)' },
  { value: 'staffing', label: '必要人員の設定' },
  { value: 'attendance', label: '勤怠設定 (割増率・休憩ルール等)' },
  { value: 'compensation', label: '給与単価の設定' },
  { value: 'leave', label: '休暇管理の設定 (休暇の種類・有休の付与基準日等)' },
  // 申請ワークフロー MECE再整理 増分⑩ (D-U41)
  { value: 'request', label: '申請・承認フローの設定 (承認パターン・申請種別・申請項目等)' },
  // 人・組織機能 増分⑦ (D-662): 「人材像」区分。既存 staffing (必要人員=何人要るか) とは別概念。
  { value: 'competency', label: '人材像・スキル評価の設定 (業務・評価尺度・面・役割の要件等)' },
  { value: 'all', label: 'シフト・労務まわりの設定全般' },
];
const NARROW_SCOPE_OPTIONS = SCOPE_OPTIONS.filter((o) => o.value !== 'all');
const scopeLabel = (scope: Scope): string => SCOPE_OPTIONS.find((o) => o.value === scope)?.label ?? scope;

const STATUS_LABELS: Record<SessionStatus, string> = {
  in_progress: '進行中', proposed: '提案あり (未適用)', applied: '適用済み', abandoned: '中断',
};
const STATUS_BADGE_CLASS: Record<SessionStatus, string> = {
  in_progress: 'bg-alert-bg text-alert-text border-alert-border',
  proposed: 'bg-alert-bg text-alert-text border-alert-border',
  applied: 'bg-success-bg text-success-text border-success-border',
  abandoned: 'bg-surface-sunken text-text-sub border-surface-border',
};

// ⚠️ target_kind はシステム内部の語彙のため画面には出さず、業務の言葉のカテゴリ名に読み替える (§6-2b)
const CATEGORY_LABELS: Record<string, string> = {
  inheritable_setting: '設定値',
  master_row: 'マスタ項目',
  condition_set: '絞り込み条件',
  attendance_setting: '勤怠設定',
  staffing_constraint: '必要人員の条件',
  shift_pattern: 'シフトパターン',
  assignment_stage: '下書き生成の設定',
  leave_type: '休暇の種類',
  approval_recipient: '承認者・共有先',
  report_formula: 'レポートの計算式',
  // v0.4 追加要件⑳-3 (D-596)
  work_record_approver: '勤務実績の承認者',
  // 申請ワークフロー MECE再整理 増分⑩ (D-U41)
  approval_workflow_pattern: '承認パターン',
  request_type: '申請種別',
  request_field: '申請項目',
  request_field_visibility_rule: '項目の表示ルール',
  // 人・組織機能 増分⑥ (D-702)
  competency_pay_policy: '処遇テーブル',
  // 人・組織機能 増分⑦ (D-664/D-665)
  evaluation_scale: '評価尺度',
  competency_face: '面 (業務×抽象度)',
  // v0.5 ② 工程⑨b (D-745)
  taxonomy_edge: '階層の親子関係',
  // v0.5 AIセットアップ条件設定支援 フェーズB-1
  visibility_rule: '表示レベルの紐づけ',
  // v0.5 AIセットアップ条件設定支援 フェーズB-2
  document_visibility: '社内文書の公開範囲',
  // v0.5 AIセットアップ条件設定支援 フェーズC-2(§8-152: web-static-dictionaries.test.tsが検出、
  // C-2実装時にこの辞書への追加が漏れていた)
  standard_rate_tier: '標準単価区分の紐づけ',
  // v0.5 AIセットアップ条件設定支援 フェーズC-3(同上、C-3実装時に漏れていた)
  allowance_eligibility: '手当支給対象の紐づけ',
};

const isSessionEndedMessage = (msg: string) => msg.includes('終了しています');

// AiOcrPage.tsx の authFetch と同型 (multipart/form-data は apiFetch の application/json 強制と
// 相容れないため、認証ヘッダ付きの生 fetch を使う)。
const authFormFetch = async (url: string, form: FormData) => {
  const token = localStorage.getItem('access_token');
  const res = await fetch(url, { method: 'POST', body: form, headers: token ? { Authorization: `Bearer ${token}` } : {} });
  handleUnauthorized(res.status);
  const body = await res.json();
  if (!res.ok || !body.success) {
    throw new ApiClientError(res.status, body.error?.code ?? 'UNKNOWN', body.error?.message ?? 'エラー');
  }
  return body.data;
};

export function AiSetupAssistantPage() {
  const qc = useQueryClient();

  const [scope, setScope] = useState<Scope>('shift');
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [sessionEnded, setSessionEnded] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [justStopped, setJustStopped] = useState(false);
  // 2026-09-17: 停止後ポーリングが完了を検知できずタイムアウトした場合の案内 (下記poll useEffect参照)
  const [stopConfirmTimedOut, setStopConfirmTimedOut] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // 「選択した◯件」/「すべて適用する」はどちらも同じ mutation を呼ぶため、配列の参照比較では
  // どちらのボタンの loading 表示か区別できない (readyIds は毎レンダー新しい配列参照になる)。
  // クリック時に明示的に記録する
  const [applyIntent, setApplyIntent] = useState<'selected' | 'all' | null>(null);
  const [showNarrowPicker, setShowNarrowPicker] = useState(false);

  // PDF/画像添付読み取り (§5): クライアント側の変換〜アップロード進捗と、サーバー側ジョブの状態は
  // 別々に管理する (§5-2/§5-3、既存の§8-114ポーリング(justStopped)とはstateを共有しない)。
  const [attachProgress, setAttachProgress] = useState<{ phase: 'convert' | 'upload'; done: number; total: number } | null>(null);
  const [attachmentJob, setAttachmentJob] = useState<AttachmentJob | null>(null);
  const [attachmentTimedOut, setAttachmentTimedOut] = useState(false);
  const [cancelAttachmentConfirm, setCancelAttachmentConfirm] = useState(false);
  const attachmentPollAttemptsRef = useRef(0);

  const messagesContainerRef = useRef<HTMLDivElement | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const pendingAssistantTextRef = useRef('');
  // ⚠️ user実機検証指摘 (2026-08-24): 「途中から再開しようとしたら画面がスクロールできず、
  // 以前のチャットのやり取りが見れなくなる」不具合。原因は下の自動スクロールeffectが常に無条件で
  // 最下部へ強制していたこと (ユーザーが手動で上にスクロールして過去のやり取りを読もうとしても、
  // messages/sending が変化するたびに最下部へ引き戻されていた)。AiMenuChatPage.tsx/
  // ProfileBuilderChatPage.tsx で既に確立済みの「ユーザーが最下部付近にいる時だけ追従する」パターン
  // (lib/chat-scroll.ts、v0.3で2回連続本番発生した既知のバグクラス) をこの画面にも移植する。
  const stickToBottomRef = useRef(true);
  const lastSessionIdRef = useRef<string | null>(null);
  // v0.3 のAIメニュー開発 (§8-37/§8-47) と同じ二重セーフティネットの3層目 (可視化): サーバ側 wall-clock
  // guard・クライアント側 postSSE idle timeout に加え、経過時間表示で「止まっているのでは」という不安を
  // 減らす。v0.3 で実際に15分ハングし復帰不能になった実害があったための必須要件 (§6-3)
  const sendStartedAtRef = useRef<number | null>(null);
  const [nowTick, setNowTick] = useState(() => Date.now());

  useEffect(() => {
    if (!sending) return undefined;
    const timer = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [sending]);

  // ⚠️ user実機検証指摘 (2026-08-25): 「生成を停止」した後、実際にはサーバー側 (Cloudflare Workers)
  // で生成・DB保存が完了しているのに、クライアントは自分のSSE接続を切っただけなのでその完了を
  // 一生知る術が無く「停止処理中です」が永久に表示され続けていた (§8-47と同型 — v0.3のAIメニュー
  // 開発機能で2回踏んだ「サーバー側は継続するがクライアントは見えなくなる」パターンの3例目。
  // ProfileBuilderChatPage.tsx/AiMenuChatPage.tsx は refetchInterval ポーリングで解決済みだが
  // この画面には未移植だった)。停止直後、会話履歴を数回ポーリングし、サーバー側で完了していた
  // 応答を検知したら取り込んで復帰する。
  const messagesRef = useRef(messages);
  useEffect(() => { messagesRef.current = messages; }, [messages]);
  useEffect(() => {
    if (!justStopped || !sessionId) return undefined;
    let cancelled = false;
    let attempts = 0;
    const MAX_ATTEMPTS = 30; // 4秒間隔 × 30 ≈ 2分 (通常の生成時間の上限目安に合わせる)
    let timer: ReturnType<typeof setTimeout>;
    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const res = await apiFetch<SessionMessagesResponse>(`/ai-setup/sessions/${sessionId}/messages`);
        if (cancelled) return;
        const lastServer = res.messages.at(-1);
        const knownIds = new Set(messagesRef.current.map((m) => m.id));
        if (lastServer && lastServer.role === 'assistant' && !knownIds.has(lastServer.message_id)) {
          setMessages(res.messages.map((m) => ({ id: m.message_id, role: m.role, text: m.text })));
          setJustStopped(false);
          qc.invalidateQueries({ queryKey: ['ai-setup-proposals', sessionId] });
          qc.invalidateQueries({ queryKey: ['ai-setup-topics', sessionId] });
          return;
        }
      } catch {
        // 一時的なネットワークエラー等は無視して次のポーリングへ
      }
      if (!cancelled) {
        if (attempts < MAX_ATTEMPTS) {
          timer = setTimeout(poll, 4000);
        } else {
          // 2026-09-17 user実機報告の修正: justStopped 中はtextarea/送信ボタンをdisabledにしたため
          // (下記参照)、ここで解放しないと検知できないケースで入力が永久にブロックされてしまう
          // (§8-114の教訓「検知できない場合がある」を前提に、ユーザーの操作を止めない側へ倒す)。
          // サーバー側のロックは最大10分で自己解放する (§8-115) ため、稀に再送でも409になりうるが、
          // その場合はバックエンドの説明的なエラーメッセージがそのまま案内になる。
          setJustStopped(false);
          setStopConfirmTimedOut(true);
        }
      }
    };
    timer = setTimeout(poll, 4000);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [justStopped, sessionId, qc]);
  const elapsedSec = sending && sendStartedAtRef.current != null
    ? Math.max(0, Math.floor((nowTick - sendStartedAtRef.current) / 1000))
    : null;

  // ユーザーが手動でスクロールした位置を監視し、最下部付近かどうかを常に最新化する
  useEffect(() => {
    const el = messagesContainerRef.current;
    if (!el) return undefined;
    const onScroll = () => {
      stickToBottomRef.current = isNearBottom(el.scrollHeight, el.scrollTop, el.clientHeight);
    };
    el.addEventListener('scroll', onScroll);
    return () => el.removeEventListener('scroll', onScroll);
  }, [sessionId]);

  useEffect(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    // 新しいセッションを開始/再開した直後は必ず最下部 (最新のやり取り) から見せる。
    // それ以外 (同じセッション内でのメッセージ追加) は、ユーザーが最下部付近にいる時だけ追従する —
    // 過去のやり取りを読むために上へスクロールしている最中に強制的に引き戻さないため。
    const isNewSession = lastSessionIdRef.current !== sessionId;
    lastSessionIdRef.current = sessionId;
    if (isNewSession) stickToBottomRef.current = true;
    if (stickToBottomRef.current) el.scrollTop = el.scrollHeight;
  }, [sessionId, messages.length, sending]);

  const proposalsQuery = useQuery<ProposalsResponse>({
    queryKey: ['ai-setup-proposals', sessionId],
    queryFn: () => apiFetch<ProposalsResponse>(`/ai-setup/sessions/${sessionId}/proposals`),
    enabled: sessionId != null,
  });
  const proposals = proposalsQuery.data?.proposals ?? [];
  // v0.5 AIセットアップ条件設定支援 フェーズA C2 (§3-5): AIが自分の提案ミスを訂正すると旧提案は
  // status='superseded' になる。ユーザーの画面には新しい提案だけを既定で見せ、旧提案は折りたたむ
  // (「過去の提案を見る」で展開可能、design-expert査読対象は主にここ)。
  const [showSuperseded, setShowSuperseded] = useState(false);
  const activeProposals = proposals.filter((p) => p.status !== 'superseded');
  const supersededProposals = proposals.filter((p) => p.status === 'superseded');

  const topicsQuery = useQuery<TopicsResponse>({
    queryKey: ['ai-setup-topics', sessionId],
    queryFn: () => apiFetch<TopicsResponse>(`/ai-setup/sessions/${sessionId}/topics`),
    enabled: sessionId != null,
  });
  const pendingTopics = (topicsQuery.data?.topics ?? []).filter((t) => t.status === 'pending');

  // staging実機検証指摘①③対応: 入口 (sessionId未確定時) で過去セッション一覧を表示する。
  const sessionsQuery = useQuery<SessionsResponse>({
    queryKey: ['ai-setup-sessions'],
    queryFn: () => apiFetch<SessionsResponse>('/ai-setup/sessions'),
    enabled: sessionId == null,
  });
  const pastSessions = sessionsQuery.data?.sessions ?? [];
  const totalAppliedAcrossSessions = pastSessions.reduce((sum, s) => sum + s.proposal_counts.applied, 0);

  const startSessionMut = useMutation({
    mutationFn: (s: Scope) => apiFetch<{ session_id: string; industry_template_id: string | null }>('/ai-setup/sessions', {
      method: 'POST', body: JSON.stringify({ scope: s }),
    }),
    onSuccess: (res) => {
      setSessionId(res.session_id);
      setSessionEnded(false);
      setMessages([]);
      setSelectedIds(new Set());
      setAttachmentJob(null);
      setAttachmentTimedOut(false);
      qc.invalidateQueries({ queryKey: ['ai-setup-sessions'] });
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : 'セッションの開始に失敗しました'),
  });

  // staging実機検証指摘③対応: 過去セッションの会話履歴を取得して再開する (進行中/提案ありなら
  // 対話を継続、適用済み/中断なら振り返り表示、既存の sessionEnded 分岐にそのまま乗る)。
  const resumeMut = useMutation({
    mutationFn: (id: string) => apiFetch<SessionMessagesResponse>(`/ai-setup/sessions/${id}/messages`),
    onSuccess: (res) => {
      setSessionId(res.session_id);
      setScope(res.scope);
      setSessionEnded(res.status === 'applied' || res.status === 'abandoned');
      setMessages(res.messages.map((m) => ({ id: m.message_id, role: m.role, text: m.text })));
      setSelectedIds(new Set());
      setAttachmentJob(null);
      setAttachmentTimedOut(false);
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : 'セッションの再開に失敗しました'),
  });

  const confirmMut = useMutation({
    mutationFn: (proposalId: string) => apiFetch<{ proposal_id: string; confirmed: boolean }>(
      `/ai-setup/sessions/${sessionId}/proposals/${proposalId}/confirm`, { method: 'POST' },
    ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ai-setup-proposals', sessionId] }),
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '確認に失敗しました'),
  });

  // §8-111: この提案は使わない、という却下操作。§8-110対策メモで「却下の仕組みが無くDB操作でしか
  // 消せない」と記録されていた欠落を解消する。
  const rejectMut = useMutation({
    mutationFn: (proposalId: string) => apiFetch<{ proposal_id: string; rejected: boolean }>(
      `/ai-setup/sessions/${sessionId}/proposals/${proposalId}/reject`, { method: 'POST' },
    ),
    onSuccess: (_res, proposalId) => {
      setSelectedIds((prev) => {
        if (!prev.has(proposalId)) return prev;
        const next = new Set(prev);
        next.delete(proposalId);
        return next;
      });
      qc.invalidateQueries({ queryKey: ['ai-setup-proposals', sessionId] });
      // v0.4「設定網羅性チェック」: 却下された提案が①のフィールド充足に使われていた場合、
      // その論点は自動的に未充足へ戻る (§4-1、reject時の後始末ロジックは不要だが表示の再取得は必要)
      qc.invalidateQueries({ queryKey: ['ai-setup-topics', sessionId] });
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '却下に失敗しました'),
  });

  const applyMut = useMutation({
    mutationFn: (ids: string[]) => apiFetch<ApplyResponse>(`/ai-setup/sessions/${sessionId}/apply`, {
      method: 'POST', body: JSON.stringify(ids.length > 0 ? { proposal_ids: ids } : {}),
    }),
    onSuccess: (res) => {
      // ⚠️ §8-110: 一部だけ失敗することがあるため、成功件数のみの単純なメッセージにしない
      if (res.failed.length > 0) {
        toast.error(
          `${res.applied.length}件を適用しました。${res.failed.length}件は適用できませんでした`
          + `${res.failed[0] ? ` (例: ${res.failed[0].message})` : ''}`,
        );
      } else {
        toast.success(`${res.applied.length}件の設定を適用しました`);
      }
      setSessionEnded(true);
      setSelectedIds(new Set());
      qc.invalidateQueries({ queryKey: ['ai-setup-proposals', sessionId] });
      qc.invalidateQueries({ queryKey: ['ai-setup-sessions'] });
      // design-and-codeレビュー指摘 (LOW): 一貫性のため topics クエリも無効化する
      qc.invalidateQueries({ queryKey: ['ai-setup-topics', sessionId] });
    },
    onError: (e) => {
      toast.error(e instanceof ApiClientError ? e.message : '適用に失敗しました');
      // ⚠️ §8-110 (user実機検証指摘): 失敗時にここで再取得しないと、画面上の「すべて適用する (N件)」
      // が古いままになり、再試行のたびに既に処理済みの提案IDを含む古いリストを送り続けて
      // 「指定された提案の一部が見つからないか、既に処理済みです」を無限に繰り返す詰み状態になっていた。
      qc.invalidateQueries({ queryKey: ['ai-setup-proposals', sessionId] });
      // ⚠️ 2026-09-19 user実機報告: AI_SETUP_TOPICS_UNRESOLVED はトースト(数秒で消える)+右側パネルの
      // 静的な注記でしか案内しておらず、「何をすればいいか」がユーザーに伝わらなかった(問い合わせ発生)。
      // トーストを補い、会話ログにアシスタント発言の体裁で案内を残す(実際のAI応答ではなくクライアント側で
      // 組み立てた定型文、サーバーへは保存されない一時的な表示)。
      if (e instanceof ApiClientError && e.code === 'AI_SETUP_TOPICS_UNRESOLVED') {
        setMessages((prev) => [...prev, {
          id: `local-topics-gate-${Date.now()}`, role: 'assistant',
          text: `適用の前に、いくつか確認させてください。${e.message}\n\n`
            + '例えば「◯◯は使っていません」のように会話で教えていただくと、この論点が解消されて適用できるようになります。',
        }]);
      }
    },
  });

  // PDF/画像添付読み取り: PDF→ページPNG変換 (既存 lib/pdf-to-images.ts を流用) → ジョブ作成 →
  // 各ページを逐次アップロード → 完了シグナルでQueueへenqueue (§1-2)。画像ファイル (png/jpeg) が
  // 直接選ばれた場合は変換せず単一ページとして扱う。
  const attachMut = useMutation({
    mutationFn: async (file: File) => {
      if (!sessionId) throw new Error('セッションが開始されていません');
      setAttachmentTimedOut(false);

      if (file.size > MAX_ATTACHMENT_FILE_SIZE_BYTES) {
        throw new Error(`ファイルが大きすぎます (上限 ${MAX_ATTACHMENT_FILE_SIZE_BYTES / 1024 / 1024}MB、指定ファイル ${(file.size / 1024 / 1024).toFixed(1)}MB)`);
      }

      let pages: Array<{ pageNumber: number; blob: Blob }>;
      if (file.type === 'application/pdf') {
        setAttachProgress({ phase: 'convert', done: 0, total: 1 });
        pages = await convertPdfToPngPages(file, (done, total) => setAttachProgress({ phase: 'convert', done, total }));
        if (pages.length === 0) throw new Error('PDFにページが含まれていません');
      } else {
        pages = [{ pageNumber: 1, blob: file }];
      }
      if (pages.length > MAX_ATTACHMENT_PAGES) {
        throw new Error(`添付できるページ数は${MAX_ATTACHMENT_PAGES}ページまでです (指定: ${pages.length}ページ)`);
      }

      const job = await apiFetch<{ job_id: string; status: AttachmentJobStatus }>(
        `/ai-setup/sessions/${sessionId}/attachments`, { method: 'POST' },
      );
      setAttachmentJob({ job_id: job.job_id, status: 'uploaded', page_count: null, error_message: null, created_at: '', updated_at: '' });

      setAttachProgress({ phase: 'upload', done: 0, total: pages.length });
      let uploaded = 0;
      for (const page of pages) {
        const isPdfPage = file.type === 'application/pdf';
        const ext = isPdfPage || file.type === 'image/png' ? 'png' : 'jpg';
        const form = new FormData();
        form.append('page_number', String(page.pageNumber));
        form.append('image', page.blob, `page-${page.pageNumber}.${ext}`);
        // eslint-disable-next-line no-await-in-loop -- ページ順に逐次アップロード (件数は上限20件のみ)
        await authFormFetch(`${API_BASE}/ai-setup/sessions/${sessionId}/attachments/${job.job_id}/pages`, form);
        uploaded += 1;
        setAttachProgress({ phase: 'upload', done: uploaded, total: pages.length });
      }

      const completed = await apiFetch<{ job_id: string; status: AttachmentJobStatus; page_count: number }>(
        `/ai-setup/sessions/${sessionId}/attachments/${job.job_id}/complete`, { method: 'POST' },
      );
      attachmentPollAttemptsRef.current = 0;
      setAttachmentTimedOut(false);
      setAttachmentJob({
        job_id: completed.job_id, status: completed.status, page_count: completed.page_count,
        error_message: null, created_at: '', updated_at: new Date().toISOString(),
      });
    },
    onSuccess: () => setAttachProgress(null),
    onError: (e) => {
      setAttachProgress(null);
      setAttachmentJob(null);
      toast.error(e instanceof Error ? e.message : '添付ファイルのアップロードに失敗しました');
    },
  });

  // §5-4: キャンセル手段。AiOcrPage.tsx の中止ボタン+ConfirmDialog と同型 (window.confirmは使わない、§8-116)
  const cancelAttachmentMut = useMutation({
    mutationFn: () => apiFetch(`/ai-setup/sessions/${sessionId}/attachments/${attachmentJob?.job_id}/cancel`, { method: 'POST' }),
    onSuccess: () => {
      toast.success('添付の処理を中止しました');
      setAttachmentJob(null);
      setCancelAttachmentConfirm(false);
    },
    onError: (e) => {
      toast.error(e instanceof ApiClientError ? e.message : '中止に失敗しました');
      setCancelAttachmentConfirm(false);
    },
  });

  // §5-3: 添付ジョブ専用のポーリング (既存の§8-114ポーリング(justStopped)とはstateを共有しない)。
  // ページ数に応じてタイムアウト目安を動的に調整する (固定2分値をそのまま使わない)。
  useEffect(() => {
    if (!sessionId || !attachmentJob || ATTACHMENT_TERMINAL_STATUSES.includes(attachmentJob.status)) return undefined;
    let cancelled = false;
    const maxAttempts = 20 + (attachmentJob.page_count ?? 5) * 4; // 4秒間隔、ページ数に応じて余裕を持たせる
    const POLL_INTERVAL_MS = 4000;
    let timer: ReturnType<typeof setTimeout>;
    const poll = async () => {
      if (cancelled) return;
      attachmentPollAttemptsRef.current += 1;
      try {
        const res = await apiFetch<AttachmentJob>(`/ai-setup/sessions/${sessionId}/attachments/${attachmentJob.job_id}`);
        if (cancelled) return;
        setAttachmentJob(res);
        if (res.status === 'completed') {
          qc.invalidateQueries({ queryKey: ['ai-setup-proposals', sessionId] });
          qc.invalidateQueries({ queryKey: ['ai-setup-topics', sessionId] });
          const msgRes = await apiFetch<SessionMessagesResponse>(`/ai-setup/sessions/${sessionId}/messages`);
          if (!cancelled) setMessages(msgRes.messages.map((m) => ({ id: m.message_id, role: m.role, text: m.text })));
          return;
        }
        if (res.status === 'failed' || res.status === 'cancelled') return;
      } catch {
        // 一時的なネットワークエラー等は無視して次のポーリングへ
      }
      if (cancelled) return;
      if (attachmentPollAttemptsRef.current < maxAttempts) {
        timer = setTimeout(poll, POLL_INTERVAL_MS);
      } else {
        // §5-3: 固定2分値ではなくページ数に応じたタイムアウト目安に達した場合、「処理中」のまま
        // 永久に待たせず、後で確認するよう案内する (UXレビューCRITICAL、§8-114/§8-47型の再発防止)
        setAttachmentTimedOut(true);
      }
    };
    timer = setTimeout(poll, POLL_INTERVAL_MS);
    return () => { cancelled = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- attachmentJob.status の変化のみで再評価すれば十分
  }, [sessionId, attachmentJob?.job_id, attachmentJob?.status, qc]);

  const attachmentBusy = attachProgress !== null
    || (attachmentJob !== null && !ATTACHMENT_TERMINAL_STATUSES.includes(attachmentJob.status));

  const resetToPicker = () => {
    if (sending) abortRef.current?.abort();
    setSessionId(null);
    setSessionEnded(false);
    setMessages([]);
    setSelectedIds(new Set());
    setInput('');
    setAttachProgress(null);
    setAttachmentJob(null);
    setAttachmentTimedOut(false);
    qc.invalidateQueries({ queryKey: ['ai-setup-sessions'] });
  };

  const send = async (content: string) => {
    const trimmed = content.trim();
    // ⚠️ 2026-09-17 user実機報告: 「停止」直後はサーバー側 (§8-115のclaim) がまだ解放されておらず
    // 409 (AI_SETUP_MESSAGE_IN_PROGRESS) になりうる。justStopped はその解放待ちポーリング中を表す
    // ため、ここでもガードする (下のtextarea/送信ボタンのdisabledと二重に防ぐ、Enter直下送信経路対策)。
    if (!trimmed || !sessionId || sessionEnded || sending || justStopped) return;
    setInput('');
    setSending(true);
    setJustStopped(false);
    setStopConfirmTimedOut(false);
    sendStartedAtRef.current = Date.now();
    pendingAssistantTextRef.current = '';
    const localMessageId = `local-${Date.now()}`;
    setMessages((prev) => [...prev, { id: localMessageId, role: 'user', text: trimmed }]);
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      await postSSE(`/ai-setup/sessions/${sessionId}/messages`, { content: trimmed }, {
        onEvent: (event, data) => {
          if (event === 'delta') {
            // ⚠️ この画面のSSEは非ストリーミング (ヒアリング対話は文字単位描画が不要、routes/ai-setup.ts
            // のコメント参照)。delta は完成済みの全文が1回だけ届く
            pendingAssistantTextRef.current = (JSON.parse(data) as { text: string }).text;
          } else if (event === 'done') {
            const parsed = JSON.parse(data) as { message_id: string; proposal_ids: string[] };
            setMessages((prev) => [...prev, { id: parsed.message_id, role: 'assistant', text: pendingAssistantTextRef.current }]);
            if (parsed.proposal_ids.length > 0) qc.invalidateQueries({ queryKey: ['ai-setup-proposals', sessionId] });
            // v0.4「設定網羅性チェック」: record_topic_status ツール呼び出しは proposal_ids を伴わないため、
            // トピック状態は毎ターン無条件で再取得する (propose_setting による①の自動充足も含む)
            qc.invalidateQueries({ queryKey: ['ai-setup-topics', sessionId] });
          } else if (event === 'error') {
            toast.error((JSON.parse(data) as { message: string }).message);
            // サーバーは失敗時にエラーを示すassistant行を保存する (§8-242)。開き直さなくても見えるよう取り直す
            apiFetch<SessionMessagesResponse>(`/ai-setup/sessions/${sessionId}/messages`)
              .then((res) => {
                setMessages(res.messages.map((m) => ({ id: m.message_id, role: m.role, text: m.text })));
                // 失敗前に確定した提案がありうるため提案一覧も取り直す
                qc.invalidateQueries({ queryKey: ['ai-setup-proposals', sessionId] });
              })
              .catch(() => { /* 取り直しの失敗は無視 (開き直せば表示される) */ });
          }
        },
      }, controller.signal);
    } catch (e) {
      if (!controller.signal.aborted) {
        // ⚠️ staging実機検証指摘 (2026-08-24): 送信失敗時 (レート制限の429等、サーバー側で
        // ai_setup_messages への保存自体が行われない) でもローカルの楽観的追加メッセージが
        // 残ったままだったため、画面遷移して戻る (DB由来の履歴で上書きされる) と「入力した内容が
        // 消えた」ように見えていた。失敗時はローカル表示からも取り除き、入力欄へ文面を復元する
        // (再送しやすくする)。
        setMessages((prev) => prev.filter((m) => m.id !== localMessageId));
        setInput(trimmed);
        const msg = e instanceof Error ? e.message : 'AI応答の取得に失敗しました';
        if (isSessionEndedMessage(msg)) setSessionEnded(true);
        toast.error(msg);
      }
    } finally {
      setSending(false);
    }
  };

  const stop = () => {
    abortRef.current?.abort();
    setJustStopped(true);
    setStopConfirmTimedOut(false);
  };

  // ⚠️ user staging実機検証指摘 (2026-08-25): 却下は取消手段の無い操作のため誤操作防止の確認は必要だが、
  // window.confirm (ネイティブダイアログ) はモバイルブラウザで同一ページから複数回呼ぶと "このページでの
  // 追加ダイアログをブロック" が有効になり、以降サイレントに何も起きなくなることがある (Safari等)。
  // 会話が長く却下対象が複数件になりうるこの画面では実際に発生した。既存の ConfirmDialog (Modal.tsx、
  // AiOcrPage.tsx等で確立済みのアプリ内確認パターン) に置き換える。
  const [rejectTarget, setRejectTarget] = useState<string | null>(null);
  const handleReject = (proposalId: string) => setRejectTarget(proposalId);
  const confirmReject = () => {
    if (rejectTarget) rejectMut.mutate(rejectTarget);
    setRejectTarget(null);
  };

  const toggleSelected = (id: string) => setSelectedIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const proposedCount = proposals.filter((p) => p.status === 'proposed').length;
  // QA MEDIUM反映: 「すべて適用する」を空配列 (=バックエンド側で全proposed対象) のまま送ると、
  // 未確認のsensitive提案が1件でもあると常に409で弾かれ「確認する」ボタンへ気づきにくい体験になる。
  // ここで selectable (= proposed かつ sensitiveなら確認済み) な提案IDだけに事前絞り込みする。
  const readyIds = proposals.filter((p) => p.status === 'proposed' && !(p.ai_write_tier === 'sensitive' && !p.confirmed_at)).map((p) => p.proposal_id);

  if (!sessionId) {
    const startingAll = startSessionMut.isPending && startSessionMut.variables === 'all';
    const startingNarrow = startSessionMut.isPending && startSessionMut.variables !== 'all';
    return (
      <div className="max-w-2xl space-y-6">
        <div className="flex items-center gap-2">
          <Sparkles size={18} aria-hidden className="text-text-sub shrink-0" />
          <h2 className="font-h3 text-h3">AI セットアップアシスタント</h2>
        </div>
        <p className="text-sm text-text-sub">
          AI との会話で現在の運用を教えていただくと、必要な設定を業務の言葉で提案します。
          内容を確認してから、まとめて適用できます。
        </p>

        {/* ⚠️ design-expertレビュー指摘 (CRITICAL、ui-guidelines.md §2.2): 赤はCTAボタン1箇所のみに
            絞る (§2.1: chrome.base はヘッダー・サイドバー専用のためここでは使わない)。枠の強調は
            色ではなく境界線の太さで表現する */}
        <div className="border-2 border-surface-border bg-white rounded-xl p-4 space-y-3">
          <div>
            <p className="text-sm font-semibold text-text-body">初めて設定する場合はこちら</p>
            <p className="text-xs text-text-sub mt-1">
              シフト・必要人員・勤怠・給与単価まわりの設定を、AI が一通り質問しながら網羅的に進めます。
              話す内容は多くなりますが、抜け漏れを防ぐため一度でまとめて進めることをおすすめします。
            </p>
          </div>
          <Button
            variant="primary"
            leftIcon={<Sparkles size={16} />}
            loading={startingAll}
            disabled={startSessionMut.isPending}
            onClick={() => { setScope('all'); startSessionMut.mutate('all'); }}
          >
            設定を一通り始める
          </Button>
        </div>

        <div className="border border-surface-border rounded-xl bg-white">
          <button
            type="button"
            onClick={() => setShowNarrowPicker((v) => !v)}
            aria-expanded={showNarrowPicker}
            className="w-full flex items-center justify-between gap-2 px-4 py-3 min-h-[44px] text-left text-sm font-medium text-text-body"
          >
            特定の設定だけを変更したい場合
            <ChevronDown size={16} aria-hidden className={`shrink-0 text-text-sub transition-transform ${showNarrowPicker ? 'rotate-180' : ''}`} />
          </button>
          {showNarrowPicker && (
            <div className="px-4 pb-4 space-y-3">
              <div>
                <label className="block text-sm text-text-sub mb-1">設定したい範囲</label>
                <Select value={scope === 'all' ? 'shift' : scope} onValueChange={(v) => setScope(v as Scope)}>
                  <SelectTrigger aria-label="設定したい範囲" className="min-h-[44px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {NARROW_SCOPE_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                variant="secondary"
                loading={startingNarrow}
                disabled={startSessionMut.isPending}
                onClick={() => startSessionMut.mutate(scope === 'all' ? 'shift' : scope)}
              >
                この範囲で開始する
              </Button>
            </div>
          )}
        </div>

        {sessionsQuery.isLoading ? (
          <p className="text-sm text-text-sub">過去のセッションを確認しています…</p>
        ) : pastSessions.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-baseline justify-between flex-wrap gap-1">
              <p className="text-sm font-semibold text-text-body">過去のセッション</p>
              <p className="text-xs text-text-sub">
                これまでに{pastSessions.length}件のセッション・累計{totalAppliedAcrossSessions}件の設定を適用済み
              </p>
            </div>
            <ul className="space-y-2">
              {pastSessions.map((s) => (
                <li key={s.session_id}>
                  <button
                    type="button"
                    onClick={() => resumeMut.mutate(s.session_id)}
                    disabled={resumeMut.isPending}
                    // ⚠️ design-expertレビュー指摘 (MEDIUM): aria-label が無いと、DOM順の断片
                    // (スコープ・状態バッジ・相対時刻・氏名・件数) がそのまま連結され読み上げられて
                    // しまうため、要点を短くまとめたラベルを明示する
                    aria-label={`${scopeLabel(s.scope)}、${STATUS_LABELS[s.status] ?? s.status}、${formatElapsedSince(s.updated_at)}`}
                    className="w-full text-left border border-surface-border rounded-lg bg-white p-3 hover:bg-surface-sunken min-h-[44px] disabled:opacity-50"
                  >
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <span className="text-sm font-medium text-text-body">
                        {scopeLabel(s.scope)}
                      </span>
                      <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full border ${STATUS_BADGE_CLASS[s.status]}`}>
                        {STATUS_LABELS[s.status] ?? s.status}
                      </span>
                    </div>
                    <p className="text-xs text-text-sub mt-1">
                      {formatElapsedSince(s.updated_at)}・{s.started_by_display_name ?? s.started_by_user_id}
                      {s.proposal_counts.total > 0 && `・提案${s.proposal_counts.total}件 (適用済み${s.proposal_counts.applied}件)`}
                    </p>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-4">
      {/* 会話ペイン */}
      <div className="flex flex-col border border-surface-border rounded-xl bg-white shadow-sm overflow-hidden min-w-0">
        <div className="px-4 py-3 border-b border-surface-line flex items-center gap-2">
          <button
            type="button"
            onClick={resetToPicker}
            // ⚠️ design-and-codeレビュー指摘 (MEDIUM): 「戻る」は本来「一覧を見るための移動」であり
            // 「今の対話を破棄する」とは別の意図。resetToPicker は sending 中は生成を abort する副作用を
            // 持つため、生成中は無効化し、まず「生成を停止」ボタンで明示的に止めてから戻らせる
            // ⚠️ 実装完了後レビュー指摘 (UX Major): 添付処理中 (attachmentBusy) に戻ると、バックグラウンドの
            // アップロード/ポーリングが止まらないままジョブが孤立して見えなくなる。まず「中止する」で
            // 明示的にジョブを止めてから戻らせる (sending と同じ考え方)
            disabled={sending || attachmentBusy}
            aria-label="セッション一覧に戻る"
            title={
              sending ? '生成が完了してから戻れます (先に停止することもできます)'
                : attachmentBusy ? '添付の処理が完了してから戻れます (先に中止することもできます)'
                  : 'セッション一覧に戻る'
            }
            className="shrink-0 min-h-[44px] min-w-[44px] -ml-2 inline-flex items-center justify-center rounded text-text-sub hover:text-text-body hover:bg-surface-sunken disabled:opacity-30 disabled:pointer-events-none focus-ring"
          >
            <ArrowLeft size={16} />
          </button>
          <Sparkles size={18} aria-hidden className="text-text-sub shrink-0" />
          <span className="font-h3 text-h3 truncate">
            AI セットアップアシスタント ({scopeLabel(scope)})
          </span>
        </div>

        <div ref={messagesContainerRef} className="flex-1 min-h-[320px] max-h-[55vh] overflow-y-auto p-4 space-y-4">
          {messages.length === 0 && (
            <div className="text-sm text-text-muted text-center py-8">
              現在の運用について話しかけてください (例:「半月ごとにシフトを組んでいます」)。
              AI が必要な設定を質問しながら、提案としてまとめます。
            </div>
          )}
          {messages.map((m) => (
            <div key={m.id} className={m.role === 'user' ? 'flex justify-end' : 'flex justify-start'}>
              <div
                className={`max-w-[80%] rounded-xl px-4 py-2 text-sm whitespace-pre-wrap ${m.role === 'user' ? 'bg-brand-red text-white' : 'bg-surface-sunken text-text-main'}`}
                {...(m.role === 'assistant' ? { 'aria-live': 'polite' as const } : {})}
              >
                {m.text || <span className="text-text-muted italic">(応答内容を取得できませんでした)</span>}
              </div>
            </div>
          ))}
          {sending && (
            <div role="status" aria-live="polite" className="flex justify-start items-center gap-2 text-xs text-text-muted">
              <Loader2 size={16} className="animate-spin" />
              <span className="tabular-nums">
                AIが応答を生成中です{elapsedSec != null ? ` (経過${formatElapsedDuration(elapsedSec)})` : ''}
                {elapsedSec != null && elapsedSec > 90 ? ' もう少しお待ちください' : ''}
              </span>
            </div>
          )}
          {!sending && justStopped && (
            <div role="status" aria-live="polite" className="flex justify-start items-center gap-2 text-xs text-text-muted">
              <Loader2 size={14} className="animate-spin" /> 停止処理中です…(この間は新しいメッセージを送信できません)
            </div>
          )}
          {!sending && !justStopped && stopConfirmTimedOut && (
            <p className="flex items-center gap-1.5 text-xs text-brand-orange">
              <AlertTriangle size={13} className="shrink-0" />
              停止処理の完了を確認できませんでした。送信するとまだ処理中エラーになる場合があります (その場合は少し待って再度お試しください)
            </p>
          )}
        </div>

        {sessionEnded ? (
          <div className="p-4 border-t border-surface-line bg-surface-sunken space-y-3">
            <p className="text-sm text-text-body">このセッションは完了しました。新しいセッションを開始しますか？</p>
            <Button variant="primary" leftIcon={<Plus size={16} />} onClick={resetToPicker}>
              新しいセッションを開始する
            </Button>
          </div>
        ) : (
          <div className="p-3 border-t border-surface-line space-y-2">
            {/* PDF/画像添付読み取り (§5): 変換〜アップロード〜Queue処理中の進捗を、既存のsending状態とは
                別に表示する (§5-2/§5-3)。クリックした瞬間にonCaptureでアップロードを開始する。 */}
            {attachProgress ? (
              <div className="text-xs text-text-sub">
                <div className="flex items-center gap-2">
                  <Loader2 size={14} className="animate-spin" />
                  {attachProgress.phase === 'convert' ? 'PDFをページ画像に変換中…' : 'ページ画像をアップロード中…'}
                  {' '}({attachProgress.done}/{attachProgress.total})
                </div>
                <div className="mt-1 h-1.5 rounded-full bg-surface-sunken overflow-hidden">
                  <div
                    className="h-full bg-brand-red transition-all"
                    style={{ width: `${attachProgress.total > 0 ? (attachProgress.done / attachProgress.total) * 100 : 0}%` }}
                  />
                </div>
              </div>
            ) : attachmentJob && !ATTACHMENT_TERMINAL_STATUSES.includes(attachmentJob.status) ? (
              <div className="flex items-center justify-between gap-2 flex-wrap text-xs text-text-sub">
                <span className="flex items-center gap-2">
                  <Loader2 size={14} className="animate-spin" />
                  添付ファイルを処理しています{attachmentJob.page_count ? ` (${attachmentJob.page_count}ページ)` : ''}…
                </span>
                <button
                  type="button"
                  onClick={() => setCancelAttachmentConfirm(true)}
                  className="text-amber-700 hover:text-warning-text underline min-h-[44px] sm:min-h-0"
                >
                  中止
                </button>
              </div>
            ) : attachmentJob?.status === 'failed' ? (
              <p className="flex items-center gap-1.5 text-xs text-alert-text">
                <AlertTriangle size={13} className="shrink-0" />
                添付ファイルの処理に失敗しました{attachmentJob.error_message ? `: ${attachmentJob.error_message}` : ''}
              </p>
            ) : null}
            {attachmentTimedOut && (
              <p className="flex items-center gap-1.5 text-xs text-brand-orange">
                <AlertTriangle size={13} className="shrink-0" />
                処理に時間がかかっています。しばらくしてから再度画面を開いてください
              </p>
            )}
            {/* 2026-09-17 user実機報告: 添付は選択した瞬間に送信されテキストを同時に送れないため、
                「添付だけされて指示が伝わらない」ように見えていた。実際は添付前に送ったメッセージが
                履歴として添付処理にも引き渡されるため、先に指示を送ってから添付すれば伝わる
                (lib/ai-setup/process-attachment-job.ts 参照)。この使い方をヒントで明示する。 */}
            {!sending && !attachmentBusy && !justStopped && (
              <p className="text-[11px] text-text-muted">
                添付ファイルに関する具体的な依頼がある場合は、添付する前にメッセージで送ってください
              </p>
            )}
            <div className="flex gap-2 items-end">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && e.shiftKey) { e.preventDefault(); send(input); } }}
                placeholder={justStopped ? '前の応答の停止処理が完了するまでお待ちください…' : 'メッセージを入力 (Enterで改行、Shift+Enterで送信)'}
                rows={2}
                disabled={sending || attachmentBusy || justStopped}
                className="flex-1 border border-surface-border rounded px-3 py-2 text-sm resize-none disabled:opacity-50"
              />
              {/* 添付処理中はtextarea・送信ボタンだけでなく添付ボタンも一括disabledにする (§5-5)。
                  FilePickerFieldはdisabled propを持たないため、busy中はボタン自体を描画しない。 */}
              {!sending && !attachmentBusy && (
                <FilePickerField
                  label="ファイルを添付"
                  accept="application/pdf,image/png,image/jpeg"
                  onChange={() => {}}
                  onCapture={(file) => attachMut.mutate(file)}
                />
              )}
              {sending ? (
                <button
                  type="button"
                  onClick={stop}
                  className="p-2.5 rounded bg-text-main text-white hover:bg-black min-h-[44px] min-w-[44px]"
                  aria-label="生成を停止"
                  title="生成を停止"
                >
                  <Square size={18} fill="currentColor" />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => send(input)}
                  disabled={!input.trim() || attachmentBusy || justStopped}
                  className="p-2.5 rounded bg-brand-red text-white disabled:opacity-50 min-h-[44px] min-w-[44px]"
                  aria-label="送信"
                >
                  <Send size={18} />
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* 提案パネル */}
      <div className="border border-surface-border rounded-xl bg-white shadow-sm overflow-hidden flex flex-col min-w-0">
        <div className="px-4 py-3 border-b border-surface-line">
          <span className="font-h3 text-h3">設定の提案</span>
        </div>
        {!sessionEnded && pendingTopics.length > 0 && (
          // design-expertレビュー指摘: text-text-muted はこの背景でコントラスト比不足 (約2.64:1、
          // AA基準4.5:1未達) のため text-text-sub に統一。scope='all' では最大11論点になりうるため
          // max-h + overflow-y-auto で内部スクロールにする (提案一覧を視界外に押し出さない)。
          // 会話・却下のたびに内容が増減するため aria-live="polite" を付与 (他の状態表示と同じ扱い)。
          <div aria-live="polite" className="mx-3 mt-3 rounded-lg border border-surface-border bg-surface-sunken p-3 space-y-1.5">
            <p className="flex items-center gap-1.5 text-xs font-semibold text-text-body">
              <ListChecks size={14} aria-hidden className="shrink-0" />
              まだ確認できていない論点があります
            </p>
            <ul className="text-xs text-text-sub space-y-0.5 pl-[19px] list-disc max-h-32 overflow-y-auto">
              {pendingTopics.map((t) => <li key={t.topic_key}>{t.label}</li>)}
            </ul>
            <p className="text-xs text-text-sub">
              会話でこれらを確認すると解消し、適用できるようになります
            </p>
          </div>
        )}
        <div className="flex-1 max-h-[55vh] overflow-y-auto p-3 space-y-3">
          {activeProposals.length === 0 && supersededProposals.length === 0 ? (
            <EmptyState message="まだ提案はありません" description="会話が進むと、ここに設定の提案が表示されます" />
          ) : (
            activeProposals.map((p) => {
              const sensitive = p.ai_write_tier === 'sensitive';
              // §8-111 QAレビュー指摘: needsConfirm が p.status を見ていないと、却下済み(rejected)の
              // sensitive提案に「確認する」ボタンが残り続け、押すとバックエンドの
              // status!=='proposed' チェックで無意味な409エラーになる。selectable と同じく
              // status==='proposed' を条件に含める。
              const needsConfirm = sensitive && !p.confirmed_at && p.status === 'proposed';
              const selectable = p.status === 'proposed' && !needsConfirm;
              return (
                <div
                  key={p.proposal_id}
                  className={`rounded-lg border p-3 space-y-2 ${sensitive ? 'bg-alert-bg border-alert-border' : 'bg-surface-sunken border-surface-border'}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-white border border-surface-border text-text-sub">
                      {CATEGORY_LABELS[p.target_kind] ?? p.target_kind}
                    </span>
                    {p.status === 'applied' && (
                      <span className="inline-flex items-center gap-1 text-xs text-success-text font-medium">
                        <CheckCircle2 size={13} /> 適用済み
                      </span>
                    )}
                    {p.status === 'applying' && (
                      <span className="inline-flex items-center gap-1 text-xs text-text-sub">
                        <Loader2 size={13} className="animate-spin" /> 適用中…
                      </span>
                    )}
                    {p.status === 'rejected' && (
                      // design-expertレビュー指摘: text-text-muted はカード背景上でコントラスト比不足 (~3.0:1、
                      // AA基準4.5:1未達)。同列の他バッジ (text-success-text/text-sub) に揃える。
                      <span className="inline-flex items-center gap-1 text-xs text-text-sub">
                        <X size={13} /> 却下済み
                      </span>
                    )}
                  </div>

                  <p className="text-sm text-text-main whitespace-pre-wrap">{p.explanation}</p>

                  {sensitive && (
                    // ⚠️ user実機検証指摘 (2026-08-25): 「これは情報の見え方/賃金計算に影響します」だけでは
                    // 何が起きるのか非技術者に伝わらないと指摘を受けた。①なぜ他と扱いが違うのか
                    // (給与計算に使われる/誰が何を見られるかに関わる、間違えると実害が出る)、
                    // ②「確認する」を押しても即座には反映されない (まだ「適用する」を押していないため)、
                    // の2点を業務の言葉で明示する。
                    <p className="flex items-start gap-1.5 text-xs text-alert-text">
                      <ShieldAlert size={14} className="shrink-0 mt-0.5" />
                      {p.target_kind === 'attendance_setting'
                        ? 'この内容は残業代・深夜手当などの給与計算に直接使われます。金額を間違えると給与計算そのものが誤ってしまうため、内容をよくご確認ください。'
                        : 'この内容は、誰がどの情報(給与など)を見られるかに関わります。設定を間違えると見えるべきでない人に情報が見えてしまう可能性があるため、内容をよくご確認ください。'}
                      {' '}なお「確認する」はまだシステムには反映されません。実際に反映されるのは、この後
                      画面下の「適用する」ボタンを押した時です。
                    </p>
                  )}

                  {needsConfirm && (
                    <div className="flex items-center gap-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        loading={confirmMut.isPending && confirmMut.variables === p.proposal_id}
                        onClick={() => confirmMut.mutate(p.proposal_id)}
                      >
                        確認する
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        loading={rejectMut.isPending && rejectMut.variables === p.proposal_id}
                        onClick={() => handleReject(p.proposal_id)}
                      >
                        却下する
                      </Button>
                    </div>
                  )}

                  {selectable && (
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <label className="flex items-center gap-2 min-h-[44px] cursor-pointer text-sm text-text-body">
                        <input
                          type="checkbox"
                          className="h-4 w-4"
                          checked={selectedIds.has(p.proposal_id)}
                          onChange={() => toggleSelected(p.proposal_id)}
                        />
                        この提案を適用する
                      </label>
                      <Button
                        variant="ghost"
                        size="sm"
                        loading={rejectMut.isPending && rejectMut.variables === p.proposal_id}
                        onClick={() => handleReject(p.proposal_id)}
                      >
                        却下する
                      </Button>
                    </div>
                  )}
                </div>
              );
            })
          )}
          {supersededProposals.length > 0 && (
            <div className="pt-1">
              <button
                type="button"
                onClick={() => setShowSuperseded((v) => !v)}
                aria-expanded={showSuperseded}
                aria-controls="superseded-proposals-list"
                className="flex items-center gap-1 text-xs text-text-sub min-h-[44px]"
              >
                <ChevronDown size={14} aria-hidden className={showSuperseded ? 'rotate-180 transition-transform' : 'transition-transform'} />
                過去の提案を見る ({supersededProposals.length}件)
              </button>
              {showSuperseded && (
                // design-expertレビュー指摘 (Critical): opacity-70 は子孫テキストごと減衰させるため、
                // 既に AA ギリギリの text-text-sub と組み合わせるとコントラスト比が基準未達になる
                // (この画面の970-975行のコメントと同型の失敗)。薄さは背景色 (bg-surface-sunken/60) と
                // ボーダーだけで表現し、文字色自体は薄めない。
                <div id="superseded-proposals-list" className="mt-1 space-y-2">
                  {supersededProposals.map((p) => (
                    <div
                      key={p.proposal_id}
                      className="rounded-lg border border-surface-border bg-surface-sunken/60 p-3 space-y-1.5"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-white border border-surface-border text-text-sub">
                          {CATEGORY_LABELS[p.target_kind] ?? p.target_kind}
                        </span>
                        <span className="inline-flex items-center gap-1 text-xs text-text-sub">
                          <History size={13} aria-hidden /> 訂正済み
                        </span>
                      </div>
                      <p className="text-sm text-text-sub whitespace-pre-wrap">{p.explanation}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        {proposedCount > 0 && (
          <div className="p-3 border-t border-surface-line space-y-2">
            <p className="text-[11px] text-text-muted">
              適用すると、この会話は終了します (未適用の提案は後からこの画面で適用できます)
            </p>
            <div className="flex flex-col gap-2">
              <Button
                variant="primary"
                size="sm"
                disabled={selectedIds.size === 0}
                loading={applyMut.isPending && applyIntent === 'selected'}
                onClick={() => { setApplyIntent('selected'); applyMut.mutate(Array.from(selectedIds)); }}
              >
                選択した{selectedIds.size}件を適用する
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={readyIds.length === 0}
                loading={applyMut.isPending && applyIntent === 'all'}
                onClick={() => { setApplyIntent('all'); applyMut.mutate(readyIds); }}
              >
                すべて適用する ({readyIds.length}件)
              </Button>
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={rejectTarget !== null}
        title="この提案を却下しますか?"
        message="元に戻すには、会話でAIに再度提案してもらう必要があります。"
        confirmLabel="却下する"
        destructive
        loading={rejectMut.isPending}
        onConfirm={confirmReject}
        onClose={() => setRejectTarget(null)}
      />

      <ConfirmDialog
        open={cancelAttachmentConfirm}
        title="添付ファイルの処理を中止しますか？"
        message="この操作は取り消せません。中止すると、この添付から提案は作成されません。"
        confirmLabel="中止する"
        destructive
        loading={cancelAttachmentMut.isPending}
        onConfirm={() => cancelAttachmentMut.mutate()}
        onClose={() => setCancelAttachmentConfirm(false)}
      />
    </div>
  );
}
